import React, { useMemo } from 'react';
import {
  ValueAxis,
  ArgumentAxis,
  Series,
  Legend,
  CommonSeriesSettings,
} from 'devextreme-react/chart';
import type { NuvoPieChartProps } from '@nuvolo/nuux-chart/components/NuvoPieChart';
import { DashboardStatsPoint } from '#/graphql/types';
import { maxBy } from 'lodash';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import FAIcon from '#/common/components/FAIcon';
import { NuvoBarChart } from '@nuvolo/nuux-chart/components/NuvoBarChart';

export interface StackedBarChartProps {
  title?: string;
  noDataText?: string;
  dataSource: { key: string; statsPoints: DashboardStatsPoint[] }[] | undefined | null;
  startingDataColor?: NuvoPieChartProps['startingDataColor'];
  setup: {
    valueAxis: string;
    argumentAxis: string;
  };
  onItemClick?: (item: DashboardStatsPoint) => void;
  yTickInterval?: number;
  colors: { [key in string]: string };
}

const StackedBarChart: React.FC<StackedBarChartProps> = ({
  title,
  noDataText = 'No data to display!',
  dataSource,
  setup,
  onItemClick,
  startingDataColor,
  colors,
  yTickInterval,
}) => {
  const ds = useMemo(() => {
    if (!dataSource) {
      return [];
    }

    return dataSource?.map((a) =>
      a.statsPoints
        .map((statsPoint) => ({ [statsPoint.filterByValue || '']: statsPoint.value, key: a.key }))
        .reduce((acc, b) => Object.assign(acc, b), {
          total: a.statsPoints.reduce((a, b) => a + (b.value ?? 0), 0),
        }),
    );
  }, [dataSource]);

  const hasData = ds && ds.length > 0;

  if (!hasData) {
    return (
      <NuvoBox
        height="100%"
        minHeight="300px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        flex={1}>
        <FAIcon icon="shapes" size="xl" />
        <NuvoText type="body">{noDataText}</NuvoText>
      </NuvoBox>
    );
  }

  return (
    <NuvoBarChart
      showTooltip
      title={title}
      startingDataColor={startingDataColor}
      dataSource={ds}
      onPointClick={(e) => onItemClick?.(e.target.data as DashboardStatsPoint)}>
      <ValueAxis
        title={setup.valueAxis}
        tickInterval={yTickInterval}
        visualRange={{
          endValue: (yTickInterval ?? 0) + (maxBy(ds, (item) => item.total)?.total || 0),
        }}
      />
      <ArgumentAxis
        title={setup.argumentAxis}
        label={{
          rotationAngle: 45,
          indentFromAxis: 0,
          overlappingBehavior: 'rotate',
        }}
      />
      <Legend
        visible
        verticalAlignment="bottom"
        horizontalAlignment="center"
        itemTextPosition="right"
      />

      <CommonSeriesSettings argumentField="key" type="stackedbar" />
      {dataSource?.[0]?.statsPoints.map((a) => (
        <Series
          key={a.label}
          valueField={a.filterByValue}
          name={a.label}
          color={a.filterByValue ? colors[a.filterByValue] : undefined}
        />
      ))}
    </NuvoBarChart>
  );
};

export default React.memo(StackedBarChart);
